import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, UploadCloud, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#C5221F] focus:ring-1 focus:ring-[#C5221F] transition-colors duration-200";

const Field = ({ label, required, children }) => (
  <div>
    <label className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-2">
      {label} {required && <span className="text-[#C5221F]">*</span>}
    </label>
    {children}
  </div>
);

const ApplicationForm = ({ job, open, onClose }) => {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", city: "", current_company: "", current_designation: "",
    total_experience: "", relevant_experience: "", current_ctc: "", expected_ctc: "",
    notice_period: "", linkedin: "", cover_note: "", website: "",
  });
  const [resume, setResume] = useState(null);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.toLowerCase().split(".").pop();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      setErrors({ ...errors, resume: "Only PDF, DOC or DOCX files are allowed" });
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setErrors({ ...errors, resume: "Resume must be under 10 MB" });
      return;
    }
    setErrors({ ...errors, resume: null });
    setResume(f);
  };

  const validate = () => {
    const err = {};
    if (form.full_name.trim().length < 2) err.full_name = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Valid email required";
    if (form.phone.trim().length < 7) err.phone = "Required";
    if (!form.city.trim()) err.city = "Required";
    if (!resume) err.resume = "Please attach your Resume/CV";
    if (!consent) err.consent = "Consent is required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("job_id", job.id);
      fd.append("consent", consent ? "true" : "false");
      fd.append("resume", resume);
      await axios.post(`${API}/applications`, fd);
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center p-4 overflow-y-auto" data-testid="application-modal">
      <div className="absolute inset-0 bg-[#0A1128]/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl my-8 shadow-2xl">
        <div className="flex items-start justify-between px-7 py-5 border-b border-slate-200 bg-[#0A1128]">
          <div>
            <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-red-400">Apply Now</p>
            <h3 className="font-display text-lg font-bold text-white mt-1">{job.title}</h3>
            {job.location && <p className="text-xs text-slate-400 mt-0.5">{job.location}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white" data-testid="application-close-btn"><X size={20} /></button>
        </div>

        {done ? (
          <div className="p-12 text-center" data-testid="application-success">
            <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
            <p className="mt-5 font-display text-xl font-bold text-slate-900">Application received</p>
            <p className="mt-2 text-sm text-slate-600 max-w-sm mx-auto leading-relaxed">
              Thank you for applying to G. R. Engineering Private Limited. A confirmation has been sent to your email, and our HR team will review your application.
            </p>
            <button onClick={onClose} className="mt-7 bg-[#0A1128] hover:bg-[#C5221F] text-white font-display font-semibold px-7 py-3 text-xs uppercase tracking-widest transition-colors duration-300" data-testid="application-done-btn">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="p-7 space-y-5 max-h-[70vh] overflow-y-auto" data-testid="application-form">
            <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" />
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name" required>
                <input className={inputCls} value={form.full_name} onChange={set("full_name")} data-testid="app-full-name" />
                {errors.full_name && <p className="mt-1 text-xs text-[#C5221F]">{errors.full_name}</p>}
              </Field>
              <Field label="Email Address" required>
                <input type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="app-email" />
                {errors.email && <p className="mt-1 text-xs text-[#C5221F]">{errors.email}</p>}
              </Field>
              <Field label="Mobile Number" required>
                <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="app-phone" />
                {errors.phone && <p className="mt-1 text-xs text-[#C5221F]">{errors.phone}</p>}
              </Field>
              <Field label="Current City / Location" required>
                <input className={inputCls} value={form.city} onChange={set("city")} data-testid="app-city" />
                {errors.city && <p className="mt-1 text-xs text-[#C5221F]">{errors.city}</p>}
              </Field>
              <Field label="Position Applied For">
                <input className={`${inputCls} bg-slate-50`} value={job.title} readOnly data-testid="app-position" />
              </Field>
              <Field label="Current Company">
                <input className={inputCls} value={form.current_company} onChange={set("current_company")} data-testid="app-company" />
              </Field>
              <Field label="Current Designation">
                <input className={inputCls} value={form.current_designation} onChange={set("current_designation")} data-testid="app-designation" />
              </Field>
              <Field label="Total Experience">
                <input className={inputCls} value={form.total_experience} onChange={set("total_experience")} placeholder="e.g. 6 years" data-testid="app-total-exp" />
              </Field>
              <Field label="Relevant Experience">
                <input className={inputCls} value={form.relevant_experience} onChange={set("relevant_experience")} placeholder="e.g. 4 years" data-testid="app-relevant-exp" />
              </Field>
              <Field label="Current CTC">
                <input className={inputCls} value={form.current_ctc} onChange={set("current_ctc")} data-testid="app-current-ctc" />
              </Field>
              <Field label="Expected CTC">
                <input className={inputCls} value={form.expected_ctc} onChange={set("expected_ctc")} data-testid="app-expected-ctc" />
              </Field>
              <Field label="Notice Period">
                <input className={inputCls} value={form.notice_period} onChange={set("notice_period")} placeholder="e.g. 30 days" data-testid="app-notice" />
              </Field>
              <Field label="LinkedIn Profile">
                <input className={inputCls} value={form.linkedin} onChange={set("linkedin")} placeholder="https://linkedin.com/in/…" data-testid="app-linkedin" />
              </Field>
            </div>
            <Field label="Cover Note / Message">
              <textarea rows={3} className={inputCls} value={form.cover_note} onChange={set("cover_note")} data-testid="app-cover-note" />
            </Field>
            <Field label="Resume / CV (PDF, DOC, DOCX — max 10 MB)" required>
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={pickFile} data-testid="app-resume-input" />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="app-resume-btn"
                className={`w-full border-2 border-dashed px-4 py-5 flex items-center justify-center gap-3 text-sm transition-colors duration-200 ${resume ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-500 hover:border-[#C5221F] hover:text-[#C5221F]"}`}>
                <UploadCloud size={18} />
                {resume ? `${resume.name} (${(resume.size / 1024 / 1024).toFixed(2)} MB)` : "Choose your Resume/CV"}
              </button>
              {errors.resume && <p className="mt-1 text-xs text-[#C5221F]" data-testid="app-resume-error">{errors.resume}</p>}
            </Field>
            <div>
              <label className="flex items-start gap-3 cursor-pointer text-[13px] text-slate-600 leading-relaxed">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#C5221F] shrink-0" data-testid="app-consent" />
                <span>I consent to G. R. Engineering Private Limited storing and processing my information and Resume/CV for recruitment purposes. My information will be used only for recruitment.</span>
              </label>
              {errors.consent && <p className="mt-1 text-xs text-[#C5221F]">{errors.consent}</p>}
            </div>
            <button type="submit" disabled={busy} data-testid="app-submit-btn"
              className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-4 text-sm uppercase tracking-widest transition-colors duration-300 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 size={16} className="animate-spin" />} {busy ? "Submitting…" : "Submit Application"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ApplicationForm;
